import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import getApiBaseUrl from "../utils/api";

function LoginPage({ onSuccess }) {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot Password states
  const [forgotMode, setForgotMode] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const API_URL = getApiBaseUrl();

  // Safely parse API responses
  async function parseResponse(res) {
    const contentType = res.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      return await res.json();
    }

    const text = await res.text();

    return {
      msg: text || "Unexpected server response"
    };
  }

  // Extract a useful error message from the API
  function getErrorMessage(data, fallback) {
    if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      const firstError = data.errors[0];

      if (typeof firstError === "string") {
        return firstError;
      }

      if (firstError?.msg) {
        return firstError.msg;
      }

      if (firstError?.message) {
        return firstError.message;
      }
    }

    return data?.msg || data?.message || fallback;
  }

  // Reset all forgot-password state
  function resetForgotPasswordState() {
    setForgotMode(false);
    setOtpSent(false);
    setError("");
    setSuccess("");
  }

  // LOGIN
  async function handleLogin(e) {
    e.preventDefault();

    setError("");
    setSuccess("");
    setLoading(true);

    const formData = new FormData(e.currentTarget);

    const email = String(formData.get("email") || "")
      .trim()
      .toLowerCase();

    const password = String(formData.get("password") || "");

    if (!email || !password) {
      setError("Please enter your email and password.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        credentials: "include",

        headers: {
          "Content-Type": "application/json"
        },

        body: JSON.stringify({
          email,
          password
        })
      });

      const data = await parseResponse(res);

      if (!res.ok) {
        setError(
          getErrorMessage(data, "Invalid email or password.")
        );
        return;
      }

      if (onSuccess && typeof onSuccess === "function") {
        onSuccess(data?.user);
      }

      navigate("/");
    } catch (err) {
      console.error("Login error:", err);
      setError("Unable to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // GENERATE OTP
  async function handleGenerateOtp(email) {
    setError("");
    setSuccess("");
    setLoading(true);

    const cleanEmail = String(email || "")
      .trim()
      .toLowerCase();

    if (!cleanEmail) {
      setError("Please enter your email address.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",

        credentials: "include",

        headers: {
          "Content-Type": "application/json"
        },

        body: JSON.stringify({
          email: cleanEmail
        })
      });

      const data = await parseResponse(res);

      if (!res.ok) {
        setError(
          getErrorMessage(data, "Failed to send OTP.")
        );
        return;
      }

      setOtpSent(true);
      setSuccess(
        data?.msg || "OTP sent successfully. Please check your email."
      );
    } catch (err) {
      console.error("Generate OTP error:", err);
      setError("Unable to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // RESET PASSWORD
  async function handleResetPassword(email, otp, password) {
    setError("");
    setSuccess("");
    setLoading(true);

    const cleanEmail = String(email || "")
      .trim()
      .toLowerCase();

    const cleanOtp = String(otp || "").trim();

    if (!cleanEmail) {
      setError("Please enter your email address.");
      setLoading(false);
      return;
    }

    if (!cleanOtp) {
      setError("Please enter the OTP.");
      setLoading(false);
      return;
    }

    if (!password) {
      setError("Please enter your new password.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/auth/reset-password`, {
        method: "POST",

        credentials: "include",

        headers: {
          "Content-Type": "application/json"
        },

        body: JSON.stringify({
          email: cleanEmail,
          otp: cleanOtp,
          newPassword: password
        })
      });

      const data = await parseResponse(res);

      if (!res.ok) {
        setError(
          getErrorMessage(data, "Failed to reset password.")
        );
        return;
      }

      setSuccess(
        data?.msg || "Password reset successful. You can now log in."
      );

      setForgotMode(false);
      setOtpSent(false);
    } catch (err) {
      console.error("Reset password error:", err);
      setError("Unable to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // FORM SUBMISSION
  async function handleSubmit(e) {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);

    // Normal login
    if (!forgotMode) {
      return handleLogin(e);
    }

    const email = String(formData.get("email") || "")
      .trim()
      .toLowerCase();

    // Step 1: Generate OTP
    if (!otpSent) {
      return handleGenerateOtp(email);
    }

    // Step 2: Reset password
    const otp = String(formData.get("otp") || "").trim();
    const password = String(formData.get("newPassword") || "");
    const confirmPassword = String(
      formData.get("confirmPassword") || ""
    );

    if (!otp) {
      setError("Please enter the OTP.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    return handleResetPassword(email, otp, password);
  }

  return (
    <div className="login-signup-container">
      <form
        className="auth-form-box"
        onSubmit={handleSubmit}
        noValidate
      >
        <h2 className="form-title">
          {forgotMode ? "Reset Password" : "Welcome Back"}
        </h2>

        <div className="auth-subtitle">
          {forgotMode
            ? "Recover your account using OTP"
            : "Sign in to continue your journey."}
        </div>

        {/* ERROR MESSAGE */}
        {error && (
          <div
            role="alert"
            style={{
              background: "#ff525220",
              border: "1px solid #ff5252",
              color: "#ff5252",
              padding: "10px",
              borderRadius: "8px",
              marginBottom: "15px",
              textAlign: "center"
            }}
          >
            {error}
          </div>
        )}

        {/* SUCCESS MESSAGE */}
        {success && (
          <div
            role="status"
            style={{
              background: "#4caf5020",
              border: "1px solid #4caf50",
              color: "#4caf50",
              padding: "10px",
              borderRadius: "8px",
              marginBottom: "15px",
              textAlign: "center"
            }}
          >
            {success}
          </div>
        )}

        {/* EMAIL */}
        <div className="form-group">
          <label
            className="form-label"
            htmlFor="login-email"
          >
            Email Address
          </label>

          <input
            id="login-email"
            className="form-input"
            required
            type="email"
            name="email"
            placeholder="Enter your email"
            autoComplete="email"
            disabled={loading}
          />
        </div>

        {/* PASSWORD - LOGIN ONLY */}
        {!forgotMode && (
          <div className="form-group">
            <label
              className="form-label"
              htmlFor="login-password"
            >
              Password
            </label>

            <div className="input-eye-wrap">
              <input
                id="login-password"
                className="form-input"
                required
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={loading}
              />

              <button
                type="button"
                className="eye-btn"
                onClick={() =>
                  setShowPassword((value) => !value)
                }
                aria-label={
                  showPassword
                    ? "Hide password"
                    : "Show password"
                }
                disabled={loading}
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>
          </div>
        )}

        {/* OTP + PASSWORD FIELDS */}
        {forgotMode && otpSent && (
          <>
            <div className="form-group">
              <label
                className="form-label"
                htmlFor="reset-otp"
              >
                OTP
              </label>

              <input
                id="reset-otp"
                className="form-input"
                name="otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter OTP"
                maxLength={6}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label
                className="form-label"
                htmlFor="new-password"
              >
                New Password
              </label>

              <input
                id="new-password"
                className="form-input"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                placeholder="New Password"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label
                className="form-label"
                htmlFor="confirm-password"
              >
                Confirm Password
              </label>

              <input
                id="confirm-password"
                className="form-input"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                placeholder="Confirm Password"
                required
                disabled={loading}
              />
            </div>
          </>
        )}

        {/* FORGOT PASSWORD LINK */}
        {!forgotMode && (
          <div
            style={{
              textAlign: "right",
              marginBottom: "10px"
            }}
          >
            <button
              type="button"
              onClick={() => {
                setForgotMode(true);
                setOtpSent(false);
                setError("");
                setSuccess("");
              }}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "var(--accent)",
                cursor: "pointer",
                font: "inherit"
              }}
              disabled={loading}
            >
              Forgot Password?
            </button>
          </div>
        )}

        {/* MAIN BUTTON */}
        <button
          className="cta-btn"
          type="submit"
          disabled={loading}
        >
          {!forgotMode
            ? loading
              ? "Logging in..."
              : "Log In"
            : !otpSent
              ? loading
                ? "Sending OTP..."
                : "Generate OTP"
              : loading
                ? "Resetting Password..."
                : "Reset Password"}
        </button>

        {/* BACK TO LOGIN */}
        {forgotMode && (
          <div
            style={{
              marginTop: "10px",
              textAlign: "center"
            }}
          >
            <button
              type="button"
              onClick={resetForgotPasswordState}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: "var(--accent)",
                font: "inherit"
              }}
              disabled={loading}
            >
              Back to Login
            </button>
          </div>
        )}

        {/* SIGN UP */}
        <div className="auth-alt-row">
          Don’t have an account?{" "}
          <Link
            to="/signup"
            style={{
              color: "var(--accent)",
              textDecoration: "none"
            }}
          >
            Sign Up
          </Link>
        </div>
      </form>
    </div>
  );
}
export default LoginPage;